import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * A6 · VISTA RÁPIDA — el panel lateral que se abre encima del listado.
 *
 * ⚠️ NO NAVEGA. Es la razón de que exista: abrir la ficha completa saca de la
 * lista y, hasta A1, además perdía los filtros. Este panel se monta encima, el
 * listado sigue atrás con su scroll y su `?query` intactos, y cerrarlo devuelve
 * exactamente a donde se estaba. Por eso tampoco toca la URL: si lo hiciera,
 * "atrás" cerraría el panel en vez de volver a la pantalla anterior, que es lo
 * que la gente espera del botón "atrás".
 *
 * ⚠️ ESCAPE CIERRA, y el foco vuelve a donde estaba. Un panel que se abre con
 * doble clic y solo se cierra con el mouse es una trampa para quien trabaja con
 * teclado — que en un listado de trabajos es casi todo el mundo.
 *
 * El contenido lo pone cada pantalla: este componente es el marco, el cierre y
 * el comportamiento. No sabe nada de trabajos ni de clientes.
 */
export function PanelLateral({
  abierto,
  onClose,
  titulo,
  subtitulo,
  children,
  pie,
}: {
  abierto: boolean;
  onClose: () => void;
  titulo: string;
  subtitulo?: string;
  children: React.ReactNode;
  /** Las acciones frecuentes. Van fijas abajo, siempre visibles. */
  pie?: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Dónde estaba el foco antes de abrir, para devolverlo al cerrar.
  const foco = useRef<Element | null>(null);

  useEffect(() => {
    if (!abierto) return;
    foco.current = document.activeElement;
    const alTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', alTecla);
    // El panel toma el foco para que el lector de pantalla anuncie dónde está.
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', alTecla);
      (foco.current as HTMLElement | null)?.focus?.();
    };
  }, [abierto, onClose]);

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={titulo}>
      {/* El fondo oscurece pero deja ver la lista: lo que importa es no perder
          el contexto de dónde se estaba. */}
      <button
        type="button"
        aria-label="Cerrar la vista rápida"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        onClick={onClose}
      />
      <div
        ref={panel}
        tabIndex={-1}
        className="relative flex h-full w-full max-w-md flex-col border-l border-surface-border bg-white shadow-xl outline-none dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex items-start justify-between gap-3 border-b border-surface-border p-4 dark:border-slate-800">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {titulo}
            </h2>
            {subtitulo && (
              <p className="truncate text-xs text-slate-500 dark:text-slate-400">{subtitulo}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar (Esc)"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {pie && (
          <div className="flex flex-wrap gap-2 border-t border-surface-border p-4 dark:border-slate-800">
            {pie}
          </div>
        )}
      </div>
    </div>
  );
}
